import type { Environment } from '../types/Environment';
import type { Result, AppError } from '../../error/types';
import { ok, err } from '../../error/functions';
import { ENVIRONMENTS } from '../constants/ENVIRONMENTS';
import { getEnvironmentValue } from './getEnvironmentValue';
import { ENVIRONMENT_VALUES } from '../constants/ENVIRONMENT_VALUES';

/**
 * @group Functions
 * @summary <blockquote>Centralized utility for determining the current application environment using the Result pattern.</blockquote>
 * 
 * @remarks
 * This function provides a single source of truth for environment detection across the entire application.
 * It handles the precedence of VERCEL_ENV over NODE_ENV and returns a Result type for proper error handling.
 * 
 * Environment detection priority:
 * 1. VERCEL_ENV (takes precedence in Vercel deployments)
 * 2. NODE_ENV (fallback for local development and other deployments)
 * 
 * @returns Result containing the current environment or an AppError if environment detection fails
 * 
 * @example
 * ```typescript
 * import { getEnvironment, ok, err } from '@shared/utils';
 * 
 * const result = getEnvironment();
 * 
 * result.match({
 *   ok: (env) => {
 *     console.log(`Current environment: ${env}`);
 *   },
 *   err: (error) => {
 *     console.error(`Environment detection failed: ${error.errorMessage}`);
 *   }
 * });
 * ```
 * 
 * @example
 * ```typescript
 * // Environment variables that affect this function:
 * // VERCEL_ENV=preview    → returns Ok('preview')
 * // NODE_ENV=development  → returns Ok('development') (if VERCEL_ENV not set)
 * // NODE_ENV=production   → returns Ok('production') (if VERCEL_ENV not set)
 * // Invalid environment    → returns Err(AppError)
 * ```
 */
export function getEnvironment(): Result<Environment, AppError> {
  // VERCEL_ENV takes precedence in Vercel deployments
  const vercelEnvResult = getEnvironmentValue(ENVIRONMENT_VALUES.LOGGING.VERCEL_ENV);
  
  return vercelEnvResult.match({
    ok: (vercelEnv: string): Result<Environment, AppError> => {
      switch (vercelEnv) {
        case ENVIRONMENTS.PRODUCTION:
          return ok(ENVIRONMENTS.PRODUCTION);
        case ENVIRONMENTS.PREVIEW:
          return ok(ENVIRONMENTS.PREVIEW);
        default:
          // For any other VERCEL_ENV values, return an error
          return err({
            errorCode: 'INVALID_VERCEL_ENV',
            errorMessage: `Invalid VERCEL_ENV value: ${vercelEnv}`
          });
      }
    },
    err: (): Result<Environment, AppError> => {
      // Fallback to NODE_ENV for local development and other deployments
      const nodeEnvResult = getEnvironmentValue(ENVIRONMENT_VALUES.LOGGING.NODE_ENV);
      
      return nodeEnvResult.match({
        ok: (nodeEnv: string): Result<Environment, AppError> => {
          switch (nodeEnv) {
            case ENVIRONMENTS.PRODUCTION:
              return ok(ENVIRONMENTS.PRODUCTION);
            case ENVIRONMENTS.DEVELOPMENT:
              return ok(ENVIRONMENTS.DEVELOPMENT);
            case 'test':
              // Treat test environment as development for consistency
              return ok(ENVIRONMENTS.DEVELOPMENT);
            default:
              // For any other NODE_ENV values, return an error
              return err({
                errorCode: 'INVALID_NODE_ENV',
                errorMessage: `Invalid NODE_ENV value: ${nodeEnv}`
              });
          }
        },
        err: (): Result<Environment, AppError> => {
          // No environment variables set, return an error
          return err({
            errorCode: 'NO_ENV_VARIABLES',
            errorMessage: 'No environment variables (VERCEL_ENV or NODE_ENV) are set'
          });
        }
      });
    }
  });
} 